/**
 * Team 班组模块 API 工厂函数
 *
 * @module api/team/factory
 * @description 班组（生产结构：车间之下的作业单元）管理 API；日报班组选择器数据源
 *
 * @backend backend/app/Modules/Organization/Controllers/TeamController.php
 * @routes /api/v1/teams
 *
 * @author YSFL Team
 * @created 2026-09-21
 * @last-updated 2026-09-21
 */

import type {
  CreateTeamForm,
  Team,
  TeamAllQueryParams,
  TeamQueryParams,
  UpdateTeamForm,
  UpdateTeamStatusForm,
} from '../../types/organization';

import type { PaginationApiResponse } from '../../types/api';
import type { IRequestClient } from '../core/client';

import { API_ROUTES } from '../routes';

/**
 * 创建 Team API 实例
 *
 * @param client HTTP 请求客户端
 * @returns Team API 实例
 */
export function createTeamApi(client: IRequestClient) {
  return {
    /**
     * 获取班组列表（分页）
     */
    getList(params?: TeamQueryParams) {
      return client.get<PaginationApiResponse<Team>>(API_ROUTES.team.list, { params });
    },

    /**
     * 获取所有班组（不分页；日报班组选择器数据源，默认仅启用）
     */
    getAll(params?: TeamAllQueryParams) {
      return client.get<Team[]>(API_ROUTES.team.all, { params });
    },

    /**
     * 获取班组详情
     */
    getDetail(id: number) {
      return client.get<Team>(API_ROUTES.team.detail(id));
    },

    /**
     * 创建班组
     */
    create(data: CreateTeamForm) {
      return client.post<Team>(API_ROUTES.team.create, data);
    },

    /**
     * 更新班组（关联部门锁定不可改）
     */
    update(id: number, data: UpdateTeamForm) {
      return client.put<Team>(API_ROUTES.team.update(id), data);
    },

    /**
     * 更新启停状态
     */
    updateStatus(id: number, data: UpdateTeamStatusForm) {
      return client.put<Team>(API_ROUTES.team.updateStatus(id), data);
    },

    /**
     * 删除班组（被日报引用时后端 400 阻止）
     */
    delete(id: number) {
      return client.delete<void>(API_ROUTES.team.delete(id));
    },
  };
}

/**
 * Team API 类型
 */
export type TeamApi = ReturnType<typeof createTeamApi>;

/**
 * 默认导出
 */
export default createTeamApi;
